/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { FaDownload } from "react-icons/fa";
import { ThemeType } from "../DesignSystem/Theme/Theme";

interface QuickFormActionBarProps {
  theme: ThemeType;
  totalFiles: number;
  selectedCount: number;
  onDeleteSelected: () => void;
  onTransformAll: () => void;
  onDownloadAllZip: () => void;
}

export const QuickFormActionBar: React.FC<QuickFormActionBarProps> = ({
  theme,
  totalFiles,
  selectedCount,
  onDeleteSelected,
  onTransformAll,
  onDownloadAllZip,
}) => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: `${theme.spacing.lg} 30px`,
        background: theme.colors.backgroundTertiary,
        borderTop: `1px solid ${theme.colors.dropdownBorderColor}`,
        borderBottom: `2px solid ${theme.colors.searchMainColor}`,
        fontFamily: theme.fonts.primary,
      }}
    >
      {/* Left side - file counts and bulk selectors */}
      <div
        style={{
          display: "flex",
          gap: theme.spacing.md,
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: theme.fontSizes.medium,
            color: theme.colors.text,
            fontWeight: 500,
          }}
        >
          <strong style={{ color: theme.colors.secondary }}>
            {totalFiles}
          </strong>{" "}
          files loaded
        </span>
        <span style={{ color: theme.colors.dropdownBorderColor }}>|</span>
        <span
          style={{
            fontSize: theme.fontSizes.medium,
            color: theme.colors.text,
            fontWeight: 500,
          }}
        >
          <strong style={{ color: theme.colors.secondary }}>
            {selectedCount}
          </strong>{" "}
          selected
        </span>
      </div>

      {/* Right side - action buttons */}
      <div style={{ display: "flex", gap: theme.spacing.md }}>
        <button
          disabled={selectedCount === 0}
          onClick={onDeleteSelected}
          style={{
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            borderRadius: "6px",
            border: "none",
            fontSize: theme.fontSizes.medium,
            fontWeight: 500,
            cursor: selectedCount === 0 ? "not-allowed" : "pointer",
            transition: "all 0.2s",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            background:
              selectedCount === 0
                ? theme.colors.buttonDisabledColor
                : theme.colors.negative,
            color: theme.colors.primaryElementFontColor,
            opacity: selectedCount === 0 ? 0.5 : 1,
            fontFamily: theme.fonts.primary,
            whiteSpace: "nowrap",
            minWidth: "150px",
            maxWidth: "200px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Delete Selected ({selectedCount})
        </button>

        <button
          disabled={selectedCount === 0}
          onClick={onTransformAll}
          style={{
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            borderRadius: "6px",
            border: "none",
            fontSize: theme.fontSizes.medium,
            fontWeight: 500,
            cursor: totalFiles === 0 ? "not-allowed" : "pointer",
            transition: "all 0.2s",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            background:
              totalFiles === 0
                ? theme.colors.buttonDisabledColor
                : theme.colors.primaryButtonColor,
            color: theme.colors.primaryElementFontColor,
            opacity: totalFiles === 0 ? 0.5 : 1,
            fontFamily: theme.fonts.primary,
            whiteSpace: "nowrap",
            minWidth: "150px",
            maxWidth: "200px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Transform Selected ({selectedCount})
        </button>

        <button
          disabled={totalFiles === 0}
          onClick={onDownloadAllZip}
          style={{
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            borderRadius: "6px",
            border: `2px solid ${
              totalFiles === 0
                ? theme.colors.dropdownBorderColor
                : theme.colors.primaryButtonColor
            }`,
            fontSize: theme.fontSizes.medium,
            fontWeight: 500,
            cursor: totalFiles === 0 ? "not-allowed" : "pointer",
            transition: "all 0.2s",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            background: "transparent",
            color:
              totalFiles === 0
                ? theme.colors.secondaryTextColor
                : theme.colors.primaryButtonColor,
            opacity: totalFiles === 0 ? 0.5 : 1,
            fontFamily: theme.fonts.primary,
            whiteSpace: "nowrap",
            minWidth: "180px",
            maxWidth: "220px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <FaDownload size={14} />
          Download All as Zip
        </button>
      </div>
    </div>
  );
};
